import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Linking, RefreshControl, ScrollView, View } from 'react-native';
import { groupRecordingsByPlatform } from '@24i/st-api-connector';
import PropTypes from 'prop-types';
import Theme from 'Theme';
import { Carousel } from 'LocalGluons';
import { useStore } from 'Store';
import { isUserFreemium } from 'Utils/userUtils';
import navigationService from 'NavigationService/';
import NavigationService from 'NavigationService';
import { useRecordings } from 'LocalGluons/components/RecordingsContext/RecordingsContext';
import { t } from 'Locale';
import { logEvent } from 'AppManager';
import { handleScroll, show, setActiveMenuItem } from 'ReduxStore/webMenu';
import { useAppSelector, useAppDispatch } from 'ReduxStore/hooks';
import { ITEM_TYPES } from 'Utils';
import { PageItemTypeEnum } from '../../../options';
import ContentWrapper from '../ContentWrapper';
import defaultStyles from './Recordings.jss';
import NoRecordingsContent from './components/NoRecordingsContent';
import RecordingsNotAvailable from './components/RecordingsNotAvailable';
import { useNavigationPropsContext } from '../../Navigation/navigationPropsContext';

const routes = ['myRecordings', 'plannedRecordings', 'iptvRecordings'];

export const getRecPlatformsFromUserData = (userData) => ({
    iptv: userData?.platforms?.length > 0 ? userData.platforms.includes('IPTV') : false,
    go: userData?.platforms?.length > 0 ? userData.platforms.includes('GO') : false
});

const setTabLabel = (index, label) => {
    switch (index) {
        case 0:
            navigationService.globalParams.myRecordingsLabel = label;
            break;
        case 1:
            navigationService.globalParams.plannedRecordingsLabel = label;
            break;
        case 2:
            navigationService.globalParams.iptvRecordingsLabel = label;
            break;
        default:
            break;
    }
};

const getRecordingData = async (appManager, pageItem) => {
    const { tv } = appManager.connector;
    const { items: playlistItems, total: totalCount } = await appManager.getPlaylistItems(pageItem);
    const groupedByPlatform = groupRecordingsByPlatform(
        playlistItems.map((playlistItem) => playlistItem.data)
    );
    return {
        items: tv.groupSeries(groupedByPlatform) || [],
        totalCount
    };
};

const Recordings = ({ navigation, route }) => {
    const pageOptions = route.params;
    const { navigate } = useNavigationPropsContext();
    const { recordings } = useRecordings();
    const { appManager, userData, isIPTVAvailable, mCoreConfig } = useStore();

    const [isComponentMounted, setIsComponentMounted] = useState(false);
    const [loaded, setLoaded] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [carousels, setCarousels] = useState([]);

    const navWillFocusListener = useRef(null);
    const isFreemium = isUserFreemium(userData);
    const dispatch = useAppDispatch();
    let hasAnyContent = false;

    for (let i = 0; i < carousels.length; i++) {
        const carousel = carousels[i];
        if (carousel.items.length > 0 && loaded && !refreshing) {
            hasAnyContent = true;
            break;
        }
    }
    /**
     * Lifecycle
     */
    useEffect(() => {
        NavigationService.setAppNavigationInstance(navigation);
        carousels;
        getRecordings();
        setIsComponentMounted(true);
        navWillFocusListener.current = navigation.addListener('focus', () => {
            getRecordings();
        });

        return () => {
            if (navWillFocusListener.current) navWillFocusListener.current();
        };
    }, []);

    // Runs/refreshes, when recordings have been edited outside current screen
    useEffect(() => {
        if (isComponentMounted) {
            onRefresh();
        }
    }, [recordings]);

    /**
     * Functions
     */
    const getRecordings = useCallback(async () => {
        setLoaded(false);
        try {
            const { reference } = pageOptions;
            const { items: pageItems } = await appManager.fetchPageData(reference);
            const carouselData = await Promise.all(
                pageItems.map(async (pageItem, index) => {
                    if (pageItem.type === PageItemTypeEnum.SINGLE_ROW) {
                        const { items, totalCount } = await getRecordingData(appManager, pageItem);
                        const routeTo = routes[index];
                        const label = pageItem.label.toUpperCase();
                        setTabLabel(index, label);
                        if (items) {
                            return {
                                id: `carousel-${label}-${index}`,
                                label,
                                items,
                                routeTo,
                                totalCount
                            };
                        }
                    }

                    return null;
                })
            );

            setCarousels(carouselData.filter((crsl) => Boolean(crsl)));
        } catch (error) {
            console.log(error);
        }
        setLoaded(true);
        setRefreshing(false);
    }, []);

    const onTvGuidePress = useCallback(() => {
        const navigateAction = navigation.navigate('TvGuide', {
            routeName: 'TvGuide'
        });
        navigation.dispatch(navigateAction);
    }, []);

    const isAfterCurrentTime = (endTimeUTC) => {
        if (!endTimeUTC) return false;
        return endTimeUTC > Date.now();
    };

    const routeToDetail = useCallback((itemSelected) => {
        if (itemSelected.modelType === 'recordingSeries') {
            itemSelected.data = itemSelected.getMetaData().metaData.detail;
        }
        logEvent({
            type: 'user-flow',
            subtype: navigation.getState().routes[0].name,
            content1: 'carousel-open-detail',
            content2: itemSelected.title,
            context: '/detail'
        });
        const item =
            recordings.find(
                (itemOfRecordings) =>
                    itemOfRecordings.data.schedule.scheduleId ===
                    itemSelected.data.schedule.scheduleId
            ) || itemSelected;
        const { modelType } = item;
        const id =
            modelType === ITEM_TYPES.RECORDING ? item?.data?.schedule?.scheduleId : item?.data?.id;
        const planned = itemSelected.isPlanned();
        const platform = item?.data?.platform;
        const endTimeUTC = item?.data?.schedule?.endTimeUTC;

        //check if item in recordings is still in live and if is, behave in navigation as live item not recording
        let navigateParams;
        if (modelType !== ITEM_TYPES.RECORDING_SERIES) {
            navigateParams = {
                id,
                type: isAfterCurrentTime(endTimeUTC) ? ITEM_TYPES.TV : modelType,
                platform
            };
        } else {
            navigateParams = { id, type: modelType, isPlanned: planned };
        }
        if (!isAfterCurrentTime(endTimeUTC)) {
            navigateParams.isPlanned = planned;
        }
        navigate('Details', { item }, navigateParams);
    }, []);

    const routeToViewAll = useCallback(
        (targetTab, title) => () => {
            logEvent({
                type: 'user-flow',
                subtype: navigation.getState().routes[0].name,
                content1: 'carousel-open-detail',
                content2: title,
                context: '/detail'
            });
            const navigateAction = navigation.navigate(
                isIPTVAvailable ? 'RecordingsAllIPTV' : 'RecordingsAll'
            );

            navigation.dispatch(navigateAction);
        },
        [carousels]
    );

    const handleDeepLink = () => {
        const deepLink = mCoreConfig.orderingServiceUrl;
        Linking.openURL(deepLink);
    };

    const onRefresh = useCallback(() => {
        setRefreshing(true);
        setCarousels([]);
        getRecordings();
    }, []);

    const renderedCarousels = useMemo(
        () =>
            carousels.map((carousel) => (
                <Carousel
                    key={carousel.id}
                    testID={carousel.id}
                    style={defaultStyles.carousel}
                    title={carousel.items.length > 0 ? carousel.label : ''}
                    items={carousel.items}
                    onViewAllPress={routeToViewAll(carousel.routeTo, carousel.title)}
                    displayViewAll={carousel.items.length > 0}
                    onItemPress={routeToDetail}
                    showCount={carousel.items.length > 0}
                    overwriteCount={carousel.totalCount || false}
                    showMetadata
                />
            )),
        [carousels, routeToViewAll, routeToDetail]
    );

    if (isFreemium)
        return (
            <ContentWrapper loading={!loaded}>
                <RecordingsNotAvailable
                    onConfirm={() => handleDeepLink()}
                    confirmLabel={
                        typeof mCoreConfig.orderingServiceUrl === 'string' &&
                        mCoreConfig.orderingServiceUrl.trim() !== '' &&
                        mCoreConfig.orderingServiceUrl !== 'null'
                            ? t('BUY_PAID_PACKAGE.BTN___PACKAGE.BENEFIT')
                            : null
                    }
                />
            </ContentWrapper>
        );

    return (
        <ContentWrapper
            loading={!loaded}
            onScroll={(e) => dispatch(handleScroll(e))}
            refreshControl={
                <RefreshControl
                    colors={[Theme.primaryActiveColor]}
                    tintColor={Theme.primaryActiveColor}
                    refreshing={refreshing}
                    onRefresh={onRefresh}
                />
            }
        >
            {loaded && (
                <View
                    style={[defaultStyles.container]}
                    refreshControl={
                        <RefreshControl
                            colors={[Theme.primaryActiveColor]}
                            tintColor={Theme.primaryActiveColor}
                            refreshing={refreshing}
                            onRefresh={onRefresh}
                        />
                    }
                >
                    {!hasAnyContent && !refreshing ? (
                        <NoRecordingsContent onTvGuidePress={onTvGuidePress} />
                    ) : (
                        renderedCarousels
                    )}
                </View>
            )}
        </ContentWrapper>
    );
};

Recordings.propTypes = {
    navigation: PropTypes.shape({}).isRequired
};

export default Recordings;
